import React from 'react';
import { createPortal } from 'react-dom';
import { List as VirtualList } from 'react-window';
import {
    MessageSquare, Search, Send, ArrowDown, Clock, Check, CheckCheck,
    AlertTriangle, RotateCw, Paperclip, Smile, X as CloseIcon, FileText,
    Image as ImageIcon, Mic, Video as VideoIcon, Users, User as UserIcon,
    Shield, MapPin, Link as LinkIcon, Reply, ChevronLeft,
    ChevronRight, Download, Phone, Play,
} from 'lucide-react';
import Linkify from 'linkify-react';
import { messagerieApi, channelsApi } from '../lib/messagerieApi';
import { api, API } from '../lib/api';
import { frElapsedShort } from '../lib/formatters';
import { ROLES } from '../lib/routeConfig';
import { toastError } from '../lib/toast';

// Phase W3 Part C — Refonte UI style WhatsApp.
//
// Portée: visuellement purement. Aucun changement de contrat API (cf. note
// serveur: `association` + `rich_content` persistés par le worker pour
// supporter le regroupement album et les cartes natives).
//
// Préserve: data-testids existants (messagerie-*), flux d'auth, routing,
// backfill, unread, pipelines d'envoi/retry, masquage serveur des messages
// supprimés.
//
// Ajoute (Part C):
//   C2  Chat list virtualisé (react-window) + avatars 48px + tag canal
//       (vue "Tous les canaux") + coches outbound + preview + unread
//   C3  Vue conversation: wallpaper subtil, bulles WhatsApp avec queue,
//       séparateurs de date, marqueur "X messages non lus", réactions
//       en pill overlap sous la bulle.
//   C4  Médias: regroupement album (association.target en priorité,
//       fallback "même expéditeur < 60 s"), grille 2x2 avec overlay +N,
//       lightbox plein écran, cartes document/lien/location/contact.
//   C5  Interactions: hover menu (réaction + réponse), Enter=envoyer /
//       Shift+Enter=nouvelle ligne, bouton "↓ Nouveaux messages".
//
// Toutes les couleurs restent dans la palette Nivo (indigo #4B3FD8,
// gradient #4B3FD8→#9B7FF4, bulle outbound #EEF0FE).

// -----------------------------------------------------------------------
// Constantes & helpers
// -----------------------------------------------------------------------
const CASA_TZ = 'Africa/Casablanca';
const ROW_HEIGHT = 72;          // hauteur ConversationRow (px)
const ALBUM_KINDS = new Set(['image', 'video', 'sticker']);
const ALBUM_WINDOW_MS = 60 * 1000;
const OUTBOUND_BUBBLE = '#EEF0FE';  // Nivo-flavored (clair indigo)
const INBOUND_BUBBLE = '#FFFFFF';

// Wallpaper WhatsApp-like: motif subtil de petits traits/points en indigo
// Nivo à ~8-10 % d'opacité sur fond #F5F6FA. URL-encodé pour que le navigateur
// n'interprète pas les `<` comme des tags HTML.
const WHATSAPP_WALLPAPER = (() => {
    const svg = (
        "<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'>" +
        "<g fill='none' stroke='#4B3FD8' stroke-opacity='0.09' stroke-width='1.2' stroke-linecap='round'>" +
        "<circle cx='22' cy='22' r='2.2' fill='#4B3FD8' fill-opacity='0.08' stroke='none'/>" +
        "<circle cx='108' cy='48' r='1.8' fill='#4B3FD8' fill-opacity='0.08' stroke='none'/>" +
        "<circle cx='70' cy='110' r='2.2' fill='#4B3FD8' fill-opacity='0.07' stroke='none'/>" +
        "<circle cx='30' cy='82' r='1.6' fill='#4B3FD8' fill-opacity='0.07' stroke='none'/>" +
        "<path d='M40 60 Q50 50 60 60'/><path d='M80 85 Q90 75 100 85'/><path d='M10 100 Q20 90 30 100'/>" +
        "<path d='M90 15 L95 20 L100 15'/><path d='M55 35 L60 40 L65 35'/>" +
        "<path d='M15 50 L20 45 L25 50'/>" +
        "</g></svg>"
    );
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
})();

function initialsFrom(name, phone) {
    const src = (name || '').trim() || (phone || '').slice(-4);
    return (src.match(/[\p{L}0-9]/gu) || []).slice(0, 2).join('').toUpperCase() || '?';
}

const AVATAR_PALETTE = [
    '#4B3FD8', '#9B7FF4', '#059669', '#0891B2', '#D97706',
    '#DC2626', '#DB2777', '#7C3AED', '#0284C7', '#4E7A2E',
];

function hashStringDjb2(s) {
    let h = 5381;
    if (!s) return 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

function avatarColorFor(key) {
    if (!key) return AVATAR_PALETTE[0];
    return AVATAR_PALETTE[hashStringDjb2(String(key)) % AVATAR_PALETTE.length];
}

function Avatar({ name, phone, waId, r2Key, sizeClass = 'w-9 h-9', textClass = 'text-xs', testId }) {
    const src = r2Key ? messagerieApi.mediaHref(r2Key) : null;
    const key = waId || phone || name || '';
    const bg = avatarColorFor(key);
    const initials = initialsFrom(name, phone);
    const [imgOk, setImgOk] = React.useState(true);
    if (src && imgOk) {
        return (
            <img
                src={src}
                alt={name || phone || 'avatar'}
                data-testid={testId}
                data-avatar-color={bg}
                data-avatar-key={key}
                onError={() => setImgOk(false)}
                className={`${sizeClass} rounded-full object-cover shrink-0 border border-[#E5E7EB]`}
            />
        );
    }
    return (
        <div
            data-testid={testId}
            data-avatar-color={bg}
            data-avatar-key={key}
            className={`${sizeClass} rounded-full shrink-0 flex items-center justify-center font-semibold text-white ${textClass}`}
            style={{ backgroundColor: bg }}
        >
            {initials}
        </div>
    );
}

function relativeFromISO(iso) {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    return frElapsedShort(s);
}

function casaDayKey(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-CA', { timeZone: CASA_TZ });
}

function casaDaysAgo(msgIso, nowIso) {
    const a = casaDayKey(nowIso);
    const b = casaDayKey(msgIso);
    if (!a || !b) return NaN;
    const [ya, ma, da] = a.split('-').map(Number);
    const [yb, mb, db] = b.split('-').map(Number);
    const A = Date.UTC(ya, ma - 1, da);
    const B = Date.UTC(yb, mb - 1, db);
    return Math.round((A - B) / 86400000);
}

function formatSeparatorLabel(iso, nowIso) {
    const diff = casaDaysAgo(iso, nowIso);
    if (isNaN(diff)) return '';
    if (diff <= 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    return new Date(iso).toLocaleDateString('fr-FR', {
        timeZone: CASA_TZ, weekday: 'long', day: 'numeric', month: 'long',
    }).toLowerCase();
}

function casaTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('fr-FR', {
        timeZone: CASA_TZ, hour: '2-digit', minute: '2-digit',
    });
}

function kindFromFile(file) {
    const t = ((file && file.type) || '').toLowerCase();
    if (t.startsWith('image/')) return 'image';
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('audio/')) return 'voice';
    return 'document';
}

// WhatsApp markdown: *bold*, _italic_, ~strike~.
const MD_RE = /(\*|_|~)(?=\S)([^*_~\n]+?)(?<=\S)\1/g;
function parseWhatsAppMarkdown(text) {
    if (!text) return [];
    const parts = [];
    let cursor = 0;
    let m;
    MD_RE.lastIndex = 0;
    while ((m = MD_RE.exec(text)) !== null) {
        if (m.index > cursor) {
            parts.push({ type: 'text', text: text.slice(cursor, m.index) });
        }
        const marker = m[1];
        const inner = m[2];
        const type = marker === '*' ? 'bold' : marker === '_' ? 'italic' : 'strike';
        parts.push({ type, text: inner });
        cursor = m.index + m[0].length;
    }
    if (cursor < text.length) {
        parts.push({ type: 'text', text: text.slice(cursor) });
    }
    return parts.length ? parts : [{ type: 'text', text }];
}

const LINKIFY_OPTS = {
    target: '_blank',
    rel: 'noopener noreferrer',
    className: 'underline underline-offset-2 hover:opacity-80',
    defaultProtocol: 'https',
};

// -----------------------------------------------------------------------
// C4 — Regroupement album (côté client).
//
// Règle PRIORITAIRE : `association.target` identique (marqueur Whapi natif).
// Fallback : même expéditeur consécutif avec Δt(prev→curr) ≤ 60 s.
//
// L'input `messages` est trié chronologiquement. L'output est une liste
// mixte: soit un message classique, soit un wrapper {type:'album', items:[...]}
// pour 2+ médias groupés. Les messages d'un même album DOIVENT avoir la même
// direction (outbound/inbound) et le même jour (Casablanca) — un changement
// de jour casse l'album (sinon le séparateur de date passerait dedans).
// -----------------------------------------------------------------------
function buildAlbums(messages) {
    const out = [];
    let i = 0;
    while (i < messages.length) {
        const m = messages[i];
        const isMedia = m.has_media && m.media
            && ALBUM_KINDS.has(String(m.media.kind || '').toLowerCase());
        const kindM = isMedia ? String(m.media.kind || '').toLowerCase() : null;
        // Un sticker ne s'aggrège jamais (comportement WhatsApp natif).
        if (!isMedia || kindM === 'sticker') { out.push(m); i++; continue; }

        const assocTarget = m.association?.target || null;
        const senderKey = m.sender_wa_id || m.from_phone || `__dir_${m.direction}`;
        const dayKey = casaDayKey(m.timestamp);
        const album = [m];
        let j = i + 1;
        while (j < messages.length) {
            const n = messages[j];
            const nIsMedia = n.has_media && n.media
                && ALBUM_KINDS.has(String(n.media.kind || '').toLowerCase());
            const nKind = nIsMedia ? String(n.media.kind || '').toLowerCase() : null;
            if (!nIsMedia || nKind === 'sticker') break;
            if (n.direction !== m.direction) break;
            if (casaDayKey(n.timestamp) !== dayKey) break;

            const nAssocTarget = n.association?.target || null;
            if (assocTarget) {
                // Priorité absolue : même association.target.
                if (nAssocTarget !== assocTarget) break;
                album.push(n);
                j++;
                continue;
            }
            // Fallback : pas d'association sur l'ancre → fenêtre glissante 60 s
            // sur l'expéditeur et le direct voisin.
            if (nAssocTarget) break;
            const nSender = n.sender_wa_id || n.from_phone || `__dir_${n.direction}`;
            if (nSender !== senderKey) break;
            const prev = album[album.length - 1];
            const dt = new Date(n.timestamp).getTime() - new Date(prev.timestamp).getTime();
            if (isNaN(dt) || dt > ALBUM_WINDOW_MS || dt < 0) break;
            album.push(n);
            j++;
        }
        if (album.length > 1) {
            out.push({ __type: 'album', items: album, anchor: m });
            i = j;
        } else {
            out.push(m);
            i++;
        }
    }
    return out;
}

// -----------------------------------------------------------------------
// Root page
// -----------------------------------------------------------------------
export default function MessageriePage() {
    const [user, setUser] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [conversations, setConversations] = React.useState(null);
    const [loadingConvs, setLoadingConvs] = React.useState(true);
    const [selectedId, setSelectedId] = React.useState(null);
    const [thread, setThread] = React.useState(null);
    const [loadingThread, setLoadingThread] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    const [attachedFiles, setAttachedFiles] = React.useState([]);
    const [uploadState, setUploadState] = React.useState(null);
    const [bridgeStatus, setBridgeStatus] = React.useState(null);
    const [channelFilter, setChannelFilter] = React.useState(null);
    const [channels, setChannels] = React.useState(null);
    const [typeFilter, setTypeFilter] = React.useState('all');
    const [participantsOpen, setParticipantsOpen] = React.useState(false);
    const [groupInfo, setGroupInfo] = React.useState(null);
    const [loadingGroup, setLoadingGroup] = React.useState(false);
    const [bridgeActive, setBridgeActive] = React.useState(true);
    // C5 — citation (reply-to) préparée dans le composer.
    const [replyTo, setReplyTo] = React.useState(null);
    // C4 — lightbox global (média plein écran).
    const [lightbox, setLightbox] = React.useState(null); // {items:[{kind,r2_key,...}], index}
    // C3 — "X messages non lus" : snapshot du unread_count au moment de la
    // sélection (ensuite effacé). Permet d'afficher la ligne séparatrice
    // au-dessus du 1er message non lu.
    const unreadSnapshotRef = React.useRef({ convId: null, count: 0 });

    React.useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/messagerie/bridge-flag`, { credentials: 'include' });
                if (res.ok) {
                    const j = await res.json();
                    setBridgeActive(!!j.bridge_active);
                }
            } catch { /* silencieux */ }
        })();
    }, []);
    // eslint-disable-next-line no-unused-vars
    const _bridgeActive = bridgeActive; // reserved pour plus tard

    React.useEffect(() => {
        (async () => {
            try {
                const me = await api.me();
                setUser(me);
            } catch (e) { /* app-level auth guard already redirects */ }
        })();
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await messagerieApi.bridgeStatus();
                if (!cancelled) setBridgeStatus(res?.status || 'unreachable');
            } catch (e) {
                if (!cancelled) setBridgeStatus('unreachable');
            }
        };
        tick();
        const iv = setInterval(tick, 60000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    const canFilterByOwner = user && (user.role === ROLES.SUPER_ADMIN || user.role === ROLES.MANAGER);
    const isAdmin = canFilterByOwner;

    React.useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await channelsApi.list();
                if (!cancelled) setChannels(res.items);
            } catch (e) { /* silencieux */ }
        };
        tick();
        const iv = setInterval(tick, 30000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [isAdmin]);

    React.useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoadingConvs(true);
            try {
                const params = {};
                if (search.trim()) params.search = search.trim();
                if (isAdmin && channelFilter) params.channel_id = channelFilter;
                if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
                const res = await messagerieApi.listConversations(params);
                if (!cancelled) setConversations(res.items);
            } catch (e) {
                if (!cancelled) toastError(e?.detail || e?.message || 'Erreur de chargement');
            } finally {
                if (!cancelled) setLoadingConvs(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search, isAdmin, channelFilter, typeFilter]);

    const handleSelect = React.useCallback((id) => {
        // C3 — snapshot du unread_count pour afficher la ligne séparatrice.
        const current = (conversations || []).find((c) => c.id === id);
        unreadSnapshotRef.current = {
            convId: id,
            count: current ? (current.unread_count || 0) : 0,
        };
        setSelectedId(id);
        setReplyTo(null);
        setConversations((prev) => prev
            ? prev.map((c) => c.id === id && (c.unread_count || 0) > 0
                ? { ...c, unread_count: 0 }
                : c)
            : prev);
    }, [conversations]);

    React.useEffect(() => {
        if (!selectedId) { setThread(null); return; }
        let cancelled = false;
        (async () => {
            setLoadingThread(true);
            try {
                const res = await messagerieApi.getMessages(selectedId, { page_size: 200 });
                if (cancelled) return;
                setThread(res);
                messagerieApi.markRead(selectedId, { silent: true }).catch(() => {});
                const conv = res?.conversation;
                if (conv && conv.channel_id && conv.type !== 'group' && !conv.contact_name) {
                    messagerieApi.refreshContactName(selectedId).then((r) => {
                        if (cancelled || !r || !r.refreshed) return;
                        const newName = r.conversation?.contact_name;
                        if (!newName) return;
                        setThread((prev) => prev && prev.conversation?.id === selectedId
                            ? { ...prev, conversation: { ...prev.conversation, contact_name: newName } }
                            : prev);
                        setConversations((prev) => prev ? prev.map((c) =>
                            c.id === selectedId ? { ...c, contact_name: newName } : c
                        ) : prev);
                    }).catch(() => {});
                }
            } catch (e) {
                if (!cancelled) toastError(e?.detail || e?.message || 'Erreur de chargement du fil');
            } finally {
                if (!cancelled) setLoadingThread(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedId]);

    React.useEffect(() => {
        setParticipantsOpen(false);
        setGroupInfo(null);
    }, [selectedId]);

    const openParticipants = React.useCallback(async () => {
        const conv = thread?.conversation;
        if (!conv || conv.type !== 'group') return;
        const gid = conv.group_id || conv.wa_id;
        if (!gid) return;
        setParticipantsOpen(true);
        if (groupInfo && groupInfo.group_id === gid) return;
        setLoadingGroup(true);
        try {
            const res = await messagerieApi.getGroup(gid);
            setGroupInfo(res.group || null);
        } catch (e) {
            toastError(e?.detail || e?.message || 'Impossible de charger le groupe');
        } finally {
            setLoadingGroup(false);
        }
    }, [thread, groupInfo]);

    const submitMessage = async () => {
        const text = draft.trim();
        const channelId = thread?.conversation?.channel_id;
        if (attachedFiles.length > 0) {
            return submitMediaBatch({ text, channelId });
        }
        if (!text || !selectedId) return;
        setSending(true);
        try {
            const payload = { conversation_id: selectedId, body: text };
            if (channelId) {
                payload.channel_id = channelId;
                payload.message_type = 'text';
                payload.text_content = text;
            }
            if (replyTo && replyTo.whapi_message_id) {
                payload.reply_to_message_id = replyTo.whapi_message_id;
            }
            const res = await messagerieApi.send(payload, { silent: true });
            setDraft('');
            setReplyTo(null);
            setThread((prev) => prev ? { ...prev, items: [...prev.items, res.message] } : prev);
            setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                ...c,
                last_message_preview: text.slice(0, 100),
                last_message_timestamp: res.message.timestamp,
            } : c) : prev);
        } catch (e) {
            let msg = 'Envoi impossible';
            if (e?.detail && typeof e.detail === 'object' && typeof e.detail.detail === 'string') {
                msg = e.detail.detail;
                if (e.detail.error === 'invalid_recipient') {
                    setThread((prev) => prev
                        ? { ...prev, conversation: { ...(prev.conversation || {}), is_unreplyable: true } }
                        : prev);
                }
            } else if (typeof e?.detail === 'string') {
                msg = e.detail;
            } else if (typeof e?.message === 'string') {
                msg = e.message;
            }
            toastError(msg);
        } finally {
            setSending(false);
        }
    };

    const submitMediaBatch = async ({ text, channelId }) => {
        if (!selectedId || attachedFiles.length === 0) return;
        if (!channelId) {
            toastError('Cette conversation ne supporte pas les médias (canal legacy).');
            return;
        }
        setSending(true);
        try {
            const files = attachedFiles.slice();
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const kind = kindFromFile(file);
                setUploadState({ index: i, total: files.length, percent: 0 });
                let uploaded;
                try {
                    uploaded = await messagerieApi.uploadMedia({
                        conversationId: selectedId,
                        kind,
                        file,
                        onProgress: (pct) => setUploadState({ index: i, total: files.length, percent: pct }),
                    });
                } catch (e) {
                    toastError(e?.detail || e?.message || 'Envoi du fichier impossible');
                    return;
                }
                const payload = {
                    conversation_id: selectedId,
                    channel_id: channelId,
                    message_type: kind,
                    media_file: {
                        r2_key: uploaded.r2_key,
                        mime_type: uploaded.mime_type,
                        file_size: uploaded.file_size,
                        filename: uploaded.filename,
                    },
                };
                if (i === 0 && text) payload.text_content = text;
                if (i === 0 && replyTo && replyTo.whapi_message_id) {
                    payload.reply_to_message_id = replyTo.whapi_message_id;
                }
                let sent;
                try {
                    sent = await messagerieApi.send(payload, { silent: true });
                } catch (e) {
                    toastError(e?.detail || e?.message || 'Envoi Whapi impossible');
                    return;
                }
                setThread((prev) => prev ? { ...prev, items: [...prev.items, sent.message] } : prev);
                setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                    ...c,
                    last_message_preview: (i === 0 && text ? text : `[${kind}]`).slice(0, 100),
                    last_message_timestamp: sent.message.timestamp,
                } : c) : prev);
            }
            setDraft('');
            setReplyTo(null);
            setAttachedFiles([]);
        } finally {
            setUploadState(null);
            setSending(false);
        }
    };

    const reactToMessage = React.useCallback(async (message, emoji) => {
        if (!message || !message.id || !selectedId) return;
        try {
            await messagerieApi.react(message.id, emoji || '');
        } catch (e) {
            toastError(e?.detail || e?.message || 'Impossible d\u2019envoyer la r\u00e9action, r\u00e9essayez');
            return;
        }
        const refetch = async () => {
            try {
                const res = await messagerieApi.getMessages(selectedId, { page_size: 200 });
                setThread((prev) => {
                    if (!prev || prev.conversation?.id !== selectedId) return prev;
                    return res;
                });
            } catch (_e) { /* silencieux */ }
        };
        setTimeout(refetch, 1000);
        setTimeout(refetch, 3000);
    }, [selectedId]);

    const retryMessage = React.useCallback(async (message) => {
        if (!message || !message.id) return;
        setThread((prev) => prev
            ? { ...prev, items: prev.items.map((m) => m.id === message.id ? { ...m, status: 'sending' } : m) }
            : prev);
        try {
            const res = await messagerieApi.send(
                { conversation_id: selectedId, body: message.body || '', retry_message_id: message.id },
                { silent: true },
            );
            setThread((prev) => prev
                ? { ...prev, items: prev.items.map((m) => m.id === res.message.id ? res.message : m) }
                : prev);
            setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                ...c,
                last_message_preview: (message.body || '').slice(0, 100),
                last_message_timestamp: res.message.timestamp,
            } : c) : prev);
        } catch (e) {
            setThread((prev) => prev
                ? { ...prev, items: prev.items.map((m) => m.id === message.id ? { ...m, status: 'failed' } : m) }
                : prev);
            let msg = 'Renvoi impossible';
            if (e?.detail && typeof e.detail === 'object' && typeof e.detail.detail === 'string') {
                msg = e.detail.detail;
            } else if (typeof e?.detail === 'string') {
                msg = e.detail;
            }
            toastError(msg);
        }
    }, [selectedId]);

    // C4 — ouverture du lightbox. `items` = liste de médias naviguables
    // (ex: tous les items d'un album, ou une seule image unique).
    const openLightbox = React.useCallback((items, index = 0) => {
        if (!items || items.length === 0) return;
        setLightbox({ items, index });
    }, []);

    // C5 — "répondre à" ouvre la citation dans le composer.
    const setReplyToFn = React.useCallback((msg) => {
        if (!msg) { setReplyTo(null); return; }
        // On ne cite que des messages avec un whapi_message_id (sinon Whapi
        // ne saura pas comment router la citation).
        if (!msg.whapi_message_id && !msg.external_message_id) {
            toastError('Ce message ne peut pas être cité (identifiant absent).');
            return;
        }
        setReplyTo({
            id: msg.id,
            whapi_message_id: msg.whapi_message_id || msg.external_message_id,
            body: msg.body,
            direction: msg.direction,
            sender_display_name: msg.sender_display_name,
            sender_phone: msg.sender_phone,
            has_media: msg.has_media,
            media: msg.media,
        });
    }, []);

    const unreadSnapshot = (unreadSnapshotRef.current.convId === selectedId)
        ? unreadSnapshotRef.current.count : 0;

    return (
        <div
            data-testid="messagerie-page"
            className="h-[calc(100vh-56px)] bg-[#F5F6FA] flex overflow-hidden flex-col"
        >
            <div className="flex-1 flex overflow-hidden">
                <ConversationList
                    conversations={conversations}
                    loading={loadingConvs}
                    selectedId={selectedId}
                    onSelect={handleSelect}
                    search={search}
                    onSearchChange={setSearch}
                    canFilterByOwner={canFilterByOwner}
                    bridgeStatus={bridgeStatus}
                    isAdmin={isAdmin}
                    channels={channels}
                    channelFilter={channelFilter}
                    onChannelFilterChange={setChannelFilter}
                    userAssignedChannelId={user?.assigned_channel_id || null}
                    typeFilter={typeFilter}
                    onTypeFilterChange={setTypeFilter}
                />
                <ThreadPane
                    thread={thread}
                    loading={loadingThread}
                    onSend={submitMessage}
                    onRetry={retryMessage}
                    onReact={reactToMessage}
                    onReply={setReplyToFn}
                    replyTo={replyTo}
                    sending={sending}
                    draft={draft}
                    setDraft={setDraft}
                    currentUser={user}
                    attachedFiles={attachedFiles}
                    setAttachedFiles={setAttachedFiles}
                    uploadState={uploadState}
                    isAdmin={isAdmin}
                    channels={channels}
                    onOpenParticipants={openParticipants}
                    groupInfo={groupInfo}
                    openLightbox={openLightbox}
                    unreadSnapshot={unreadSnapshot}
                    onReassign={async (agent_user_id) => {
                        if (!selectedId) return;
                        try {
                            const res = await messagerieApi.reassign(selectedId, agent_user_id);
                            setThread((prev) => prev ? { ...prev, conversation: res.conversation } : prev);
                        } catch (e) {
                            toastError(e?.detail || e?.message || 'Réassignation impossible');
                        }
                    }}
                />
                {participantsOpen ? (
                    <ParticipantsPanel
                        group={groupInfo}
                        loading={loadingGroup}
                        onClose={() => setParticipantsOpen(false)}
                    />
                ) : null}
            </div>
            {lightbox ? (
                <Lightbox
                    items={lightbox.items}
                    index={lightbox.index}
                    onClose={() => setLightbox(null)}
                    onChange={(idx) => setLightbox((lb) => lb ? { ...lb, index: idx } : lb)}
                />
            ) : null}
        </div>
    );
}

// -----------------------------------------------------------------------
// Bridge + channels widgets
// -----------------------------------------------------------------------
function BridgeStatusDot({ status }) {
    const map = {
        ready:       { color: '#10B981', label: 'Bridge actif' },
        waiting:     { color: '#F59E0B', label: 'Bridge en attente de connexion' },
        unreachable: { color: '#EF4444', label: 'Bridge injoignable' },
    };
    const conf = map[status];
    if (!conf) {
        return (
            <span
                data-testid="bridge-status-dot"
                data-status="loading"
                title="Vérification du bridge…"
                aria-label="Vérification du bridge"
                className="inline-block w-2 h-2 rounded-full bg-[#D1D5DB]"
            />
        );
    }
    return (
        <span
            data-testid="bridge-status-dot"
            data-status={status}
            title={conf.label}
            aria-label={conf.label}
            className="inline-flex items-center gap-1.5"
        >
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: conf.color }} />
            <span
                data-testid="bridge-status-label"
                className="text-[10px] font-medium leading-none"
                style={{ color: conf.color }}
            >
                {conf.label}
            </span>
        </span>
    );
}

function ChannelStatusWidget({ channels, userAssignedChannelId, isAdmin, channelFilter, onFilter }) {
    if (!isAdmin && !userAssignedChannelId) return null;
    const items = isAdmin
        ? (channels || [])
        : (userAssignedChannelId
            ? [(channels || []).find((c) => c.channel_id === userAssignedChannelId) || { channel_id: userAssignedChannelId }]
            : []);
    return (
        <div data-testid="messagerie-channels-widget" className="flex items-center gap-1.5">
            {items.map((c) => {
                const color = c?.status === 'connected' ? '#10B981'
                    : c?.status === 'qr_required' ? '#F59E0B'
                    : '#EF4444';
                const label = `${c.channel_id}${c.commercial_name ? ' (' + c.commercial_name + ')' : ''}`;
                const active = isAdmin && channelFilter === c.channel_id;
                return (
                    <button
                        key={c.channel_id}
                        type="button"
                        data-testid="messagerie-channel-dot"
                        data-channel-id={c.channel_id}
                        data-status={c?.status || 'unknown'}
                        title={label + ' — ' + (c?.status || 'inconnu')}
                        onClick={isAdmin && onFilter ? () => onFilter(active ? null : c.channel_id) : undefined}
                        disabled={!isAdmin}
                        className={
                            'inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-medium border ' +
                            (active
                                ? 'bg-[#4B3FD8]/10 border-[#4B3FD8] text-[#4B3FD8]'
                                : 'bg-white border-[#E5E7EB] text-[#1A1D2E]') +
                            (isAdmin ? ' hover:bg-[#F5F6FA]' : ' cursor-default')
                        }
                    >
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                        {c.channel_id}
                    </button>
                );
            })}
        </div>
    );
}

// -----------------------------------------------------------------------
// C2 — ConversationList virtualisée (react-window).
// -----------------------------------------------------------------------
function ConversationList({
    conversations, loading, selectedId, onSelect,
    search, onSearchChange, canFilterByOwner, bridgeStatus,
    isAdmin, channels, channelFilter, onChannelFilterChange, userAssignedChannelId,
    typeFilter, onTypeFilterChange,
}) {
    // Vue "Tous les canaux" (admin sans filtre) → on affiche le tag canal.
    const showChannelTag = !!isAdmin && !channelFilter;
    // rowProps stable sur la référence → les lignes ne re-rendent pas inutilement.
    const rowProps = React.useMemo(() => ({
        conversations: conversations || [],
        selectedId,
        onSelect,
        showOwner: canFilterByOwner,
        showChannelTag,
    }), [conversations, selectedId, onSelect, canFilterByOwner, showChannelTag]);

    return (
        <aside
            data-testid="messagerie-list"
            className="w-[32%] min-w-[300px] max-w-[440px] shrink-0 h-full border-r border-[#E5E7EB] bg-white flex flex-col"
        >
            <div className="p-3 border-b border-[#E5E7EB] space-y-2 shrink-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[#1A1D2E]">
                        <MessageSquare size={18} strokeWidth={1.75} />
                        <h1 className="text-sm font-semibold">Messagerie</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <ChannelStatusWidget
                            channels={channels}
                            userAssignedChannelId={userAssignedChannelId}
                            isAdmin={isAdmin}
                            channelFilter={channelFilter}
                            onFilter={onChannelFilterChange}
                        />
                        <BridgeStatusDot status={bridgeStatus} />
                    </div>
                </div>
                {isAdmin ? (
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] uppercase tracking-wide text-[#6B7280] shrink-0">Canal</label>
                        <select
                            data-testid="messagerie-channel-filter"
                            value={channelFilter || ''}
                            onChange={(e) => onChannelFilterChange && onChannelFilterChange(e.target.value || null)}
                            className="flex-1 h-7 px-2 text-xs rounded-md border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                        >
                            <option value="">Tous les canaux</option>
                            {(channels || []).map((c) => (
                                <option key={c.channel_id} value={c.channel_id}>
                                    {c.channel_id}{c.commercial_name ? ` — ${c.commercial_name}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
                <div
                    data-testid="messagerie-type-toggle"
                    role="tablist"
                    className="inline-flex h-7 w-full rounded-md border border-[#E5E7EB] bg-white p-0.5"
                >
                    {[
                        { value: 'all',        label: 'Tous',        icon: null },
                        { value: 'individual', label: 'Individuels', icon: UserIcon },
                        { value: 'group',      label: 'Groupes',     icon: Users },
                    ].map((opt) => {
                        const Icon = opt.icon;
                        const active = (typeFilter || 'all') === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                data-testid={`messagerie-type-toggle-${opt.value}`}
                                data-active={active ? 'true' : 'false'}
                                onClick={() => onTypeFilterChange && onTypeFilterChange(opt.value)}
                                className={
                                    'flex-1 h-full inline-flex items-center justify-center gap-1 rounded text-[11px] font-medium transition-colors ' +
                                    (active
                                        ? 'bg-[#4B3FD8] text-white'
                                        : 'text-[#6B7280] hover:bg-[#F5F6FA]')
                                }
                            >
                                {Icon ? <Icon size={12} strokeWidth={2} /> : null}
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                <div className="relative">
                    <Search
                        size={14}
                        strokeWidth={1.75}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none"
                    />
                    <input
                        data-testid="messagerie-search"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Rechercher un nom ou un numéro…"
                        className="w-full h-8 pl-8 pr-3 text-sm rounded-md border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                    />
                </div>
            </div>
            <div data-testid="messagerie-list-scroll" className="flex-1 min-h-0">
                {loading ? <ListSkeleton /> : null}
                {!loading && conversations && conversations.length === 0 ? (
                    <div
                        data-testid="messagerie-list-empty"
                        className="p-8 text-center text-sm text-[#6B7280]"
                    >
                        Aucune conversation pour le moment. Les messages WhatsApp de vos clients apparaîtront ici.
                    </div>
                ) : null}
                {!loading && conversations && conversations.length > 0 ? (
                    <VirtualList
                        rowCount={conversations.length}
                        rowHeight={ROW_HEIGHT}
                        rowComponent={ConversationRow}
                        rowProps={rowProps}
                        style={{ width: '100%', height: '100%' }}
                        overscanCount={6}
                        aria-label="Liste des conversations"
                    />
                ) : null}
            </div>
        </aside>
    );
}

// -----------------------------------------------------------------------
// C2 — ConversationRow redesigné (style WhatsApp).
// -----------------------------------------------------------------------
function ConversationRow({ index, style, ariaAttributes, conversations, selectedId, onSelect, showOwner, showChannelTag }) {
    const conv = conversations[index];
    if (!conv) return <div style={style} {...ariaAttributes} />;
    const isGroup = conv.type === 'group';
    const displayName = isGroup
        ? (conv.group_name || conv.name || conv.contact_name || 'Groupe WhatsApp')
        : (conv.contact_name || conv.phone_number || '—');
    const preview = conv.last_message_preview || '';
    const avatarKey = conv.wa_id || conv.group_id || conv.phone_number || displayName;
    // Avatar R2 : individuel → `contact_avatar_r2_key`, groupe → `avatar_r2_key`.
    const avatarR2 = isGroup
        ? (conv.avatar_r2_key || null)
        : (conv.contact_avatar_r2_key || conv.avatar_r2_key || null);
    const selected = conv.id === selectedId;
    const unread = conv.unread_count || 0;
    // Preview indicator: outbound ✓/✓✓ (approximatif — on affiche la coche
    // seulement si le dernier message connu est outbound).
    const lastOut = conv.last_message_direction === 'outbound';
    const lastStatus = conv.last_message_status || null;

    return (
        <div style={style} {...ariaAttributes}>
            <button
                type="button"
                data-testid="messagerie-conv-row"
                data-conv-id={conv.id}
                data-conv-type={isGroup ? 'group' : 'individual'}
                data-selected={selected ? 'true' : 'false'}
                onClick={() => onSelect(conv.id)}
                className={
                    'w-full h-full text-left px-3 flex items-center gap-3 transition-colors border-b border-[#F3F4F6] ' +
                    (selected ? 'bg-[#EEF0FE]' : 'bg-white hover:bg-[#F5F6FA]')
                }
            >
                <div className="relative shrink-0">
                    <Avatar
                        name={displayName}
                        phone={conv.phone_number}
                        waId={avatarKey}
                        r2Key={avatarR2}
                        sizeClass="w-12 h-12"
                        textClass="text-sm"
                        testId="messagerie-conv-avatar"
                    />
                    {isGroup ? (
                        <span
                            data-testid="messagerie-conv-group-badge"
                            className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center text-[#4B3FD8]"
                            title="Groupe WhatsApp"
                            aria-label="Groupe WhatsApp"
                        >
                            <Users size={11} strokeWidth={2.25} />
                        </span>
                    ) : null}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                        <div className="min-w-0 flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-[#1A1D2E] truncate">{displayName}</span>
                            {showChannelTag && conv.channel_id ? (
                                <span
                                    data-testid="messagerie-conv-channel-tag"
                                    className="shrink-0 inline-flex items-center h-[16px] px-1.5 rounded text-[9px] font-semibold border border-[#4B3FD8]/40 text-[#4B3FD8] bg-[#4B3FD8]/5"
                                    title={`Canal ${conv.channel_id}`}
                                >
                                    {conv.channel_id}
                                </span>
                            ) : null}
                        </div>
                        <span className={
                            'text-[10px] shrink-0 tabular-nums ' +
                            (unread > 0 ? 'text-[#4B3FD8] font-semibold' : 'text-[#6B7280]')
                        }>
                            {relativeFromISO(conv.last_message_timestamp)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                        <div className="min-w-0 flex-1 flex items-center gap-1 text-xs text-[#6B7280]">
                            {lastOut ? (
                                <span className="inline-flex shrink-0" aria-hidden="true">
                                    {lastStatus === 'read'
                                        ? <CheckCheck size={12} strokeWidth={2} className="text-[#3B82F6]" />
                                        : lastStatus === 'delivered'
                                            ? <CheckCheck size={12} strokeWidth={2} className="text-[#6B7280]" />
                                            : lastStatus === 'failed'
                                                ? <AlertTriangle size={12} strokeWidth={2} className="text-[#EF4444]" />
                                                : <Check size={12} strokeWidth={2} className="text-[#6B7280]" />}
                                </span>
                            ) : null}
                            <span className="truncate">{preview || '…'}</span>
                        </div>
                        {unread > 0 ? (
                            <span
                                data-testid="messagerie-conv-unread"
                                className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-semibold bg-[#4B3FD8] text-white shrink-0"
                            >
                                {unread > 99 ? '99+' : unread}
                            </span>
                        ) : null}
                    </div>
                    {showOwner && conv.owner_cc ? (
                        <div className="mt-0.5">
                            <span
                                data-testid="messagerie-conv-owner"
                                className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-medium border border-[#E5E7EB] text-[#6B7280]"
                            >
                                {conv.owner_cc}
                            </span>
                        </div>
                    ) : null}
                </div>
            </button>
        </div>
    );
}

function ListSkeleton() {
    return (
        <ul className="divide-y divide-[#F3F4F6]" data-testid="messagerie-list-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="px-3 h-[72px] flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#F3F4F6] animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                        <div className="h-3 rounded bg-[#F3F4F6] w-1/2 animate-pulse" />
                        <div className="h-3 rounded bg-[#F3F4F6] w-3/4 animate-pulse" />
                    </div>
                </li>
            ))}
        </ul>
    );
}

// -----------------------------------------------------------------------
// C3 — ThreadPane (header + stream + composer)
// -----------------------------------------------------------------------
function ThreadPane({
    thread, loading, onSend, onRetry, onReact, onReply, replyTo,
    sending, draft, setDraft, currentUser,
    attachedFiles, setAttachedFiles, uploadState,
    isAdmin, channels, onReassign, onOpenParticipants, groupInfo,
    openLightbox, unreadSnapshot,
}) {
    if (!thread) {
        return (
            <section
                data-testid="messagerie-thread-empty"
                className="flex-1 h-full flex items-center justify-center text-sm text-[#6B7280] bg-[#F5F6FA]"
            >
                {loading ? 'Chargement…' : 'Sélectionnez une conversation'}
            </section>
        );
    }
    const conv = thread.conversation || {};
    const messages = thread.items || [];
    const canSeeOwner = currentUser && (currentUser.role === ROLES.SUPER_ADMIN || currentUser.role === ROLES.MANAGER);
    const isUnreplyable = conv.is_unreplyable === true;
    const unreplyableTooltip = "Ce contact n'a pas de numéro WhatsApp standard. Répondez depuis le téléphone bridge directement.";
    const isLegacyBridge = !conv.channel_id;
    const legacyBridgeNote = "Conversation archivée (ancien système) — lecture seule";
    const isGroup = conv.type === 'group';
    const groupName = groupInfo?.name || conv.group_name || conv.contact_name || 'Groupe WhatsApp';
    const groupDescription = groupInfo?.description || conv.description;
    const groupParticipantCount = groupInfo?.participant_count;
    const groupAvatarKey = conv.wa_id || conv.group_id;
    const headerAvatarR2 = isGroup
        ? (groupInfo?.avatar_r2_key || conv.avatar_r2_key || null)
        : (conv.contact_avatar_r2_key || conv.avatar_r2_key || null);
    const headerAvatarKey = isGroup ? groupAvatarKey : (conv.wa_id || conv.phone_number);
    const displayLabel = isGroup ? groupName : (conv.contact_name || conv.phone_number || conv.whatsapp_jid || '—');
    const subtitleLine = isGroup
        ? (typeof groupParticipantCount === 'number'
            ? `${groupParticipantCount} participant${groupParticipantCount > 1 ? 's' : ''}`
            : '')
        : ((conv.contact_name && (conv.phone_number || conv.whatsapp_jid)) ? (conv.phone_number || conv.whatsapp_jid) : '');

    return (
        <section data-testid="messagerie-thread" className="flex-1 h-full flex flex-col">
            {/* Header avec avatar + nom + sous-titre */}
            <header className="h-16 shrink-0 bg-white border-b border-[#E5E7EB] px-4 flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-center gap-3">
                    <Avatar
                        name={displayLabel}
                        phone={conv.phone_number}
                        waId={headerAvatarKey}
                        r2Key={headerAvatarR2}
                        sizeClass="w-10 h-10"
                        textClass="text-xs"
                        testId="messagerie-thread-avatar"
                    />
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1A1D2E] truncate flex items-center gap-1.5">
                            {isGroup ? <Users size={13} strokeWidth={2} className="text-[#4B3FD8] shrink-0" /> : null}
                            <span data-testid="messagerie-thread-title" className="truncate">{displayLabel}</span>
                        </div>
                        {isGroup && groupDescription ? (
                            <GroupDescription text={groupDescription} />
                        ) : subtitleLine ? (
                            <div className="text-[11px] text-[#6B7280] truncate">{subtitleLine}</div>
                        ) : null}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {isUnreplyable ? (
                        <span
                            data-testid="messagerie-thread-unreplyable"
                            title={unreplyableTooltip}
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
                        >
                            Non répondable
                        </span>
                    ) : null}
                    {conv.channel_id ? (
                        <span
                            data-testid="messagerie-thread-channel"
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium border border-[#4B3FD8] text-[#4B3FD8] bg-[#4B3FD8]/5"
                        >
                            {conv.channel_id}
                        </span>
                    ) : null}
                    {canSeeOwner && conv.owner_cc ? (
                        <span
                            data-testid="messagerie-thread-owner"
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium border border-[#E5E7EB] text-[#6B7280]"
                        >
                            {conv.owner_cc}
                        </span>
                    ) : null}
                    {isGroup ? (
                        <button
                            type="button"
                            data-testid="messagerie-thread-participants-btn"
                            onClick={() => onOpenParticipants && onOpenParticipants()}
                            className="inline-flex items-center gap-1 h-6 px-2 rounded border border-[#E5E7EB] text-[11px] text-[#1A1D2E] hover:bg-[#F5F6FA]"
                            title="Voir les participants"
                        >
                            <Users size={12} strokeWidth={2} />
                            Participants
                        </button>
                    ) : null}
                    {isAdmin ? (
                        <select
                            data-testid="messagerie-thread-reassign"
                            value={conv.assigned_agent_id || ''}
                            onChange={(e) => onReassign && onReassign(e.target.value || null)}
                            className="h-6 max-w-[180px] px-1.5 text-[11px] rounded border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                            title="Réassigner à un agent"
                        >
                            <option value="">— Non assigné —</option>
                            {(channels || []).filter((c) => c.agent).map((c) => (
                                <option key={c.channel_id} value={c.agent.id}>
                                    {c.channel_id} · {c.agent.display_name || c.agent.email}
                                </option>
                            ))}
                        </select>
                    ) : null}
                </div>
            </header>

            <MessageStream
                messages={messages}
                loading={loading}
                onRetry={onRetry}
                onReact={onReact}
                onReply={onReply}
                isGroup={isGroup}
                conversation={conv}
                currentUser={currentUser}
                openLightbox={openLightbox}
                unreadSnapshot={unreadSnapshot}
            />

            {isUnreplyable ? (
                <div
                    data-testid="messagerie-composer-disabled"
                    title={unreplyableTooltip}
                    className="shrink-0 border-t border-[#E5E7EB] bg-white p-3"
                >
                    <div className="h-9 px-3 rounded-lg bg-[#F5F6FA] border border-dashed border-[#E5E7EB] text-xs text-[#6B7280] flex items-center">
                        {unreplyableTooltip}
                    </div>
                </div>
            ) : isLegacyBridge ? (
                <div
                    data-testid="messagerie-composer-legacy"
                    className="shrink-0 border-t border-[#E5E7EB] bg-white p-3"
                >
                    <div
                        data-testid="messagerie-legacy-note"
                        className="h-9 px-3 rounded-lg bg-[#F5F6FA] border border-dashed border-[#E5E7EB] text-xs text-[#6B7280] flex items-center justify-center"
                    >
                        {legacyBridgeNote}
                    </div>
                </div>
            ) : (
                <MessageComposer
                    thread={thread}
                    draft={draft}
                    setDraft={setDraft}
                    onSend={onSend}
                    sending={sending}
                    attachedFiles={attachedFiles || []}
                    setAttachedFiles={setAttachedFiles || (() => {})}
                    uploadState={uploadState}
                    replyTo={replyTo}
                    onClearReply={() => onReply(null)}
                />
            )}
        </section>
    );
}

function GroupDescription({ text }) {
    const [expanded, setExpanded] = React.useState(false);
    const short = (text || '').length > 60 && !expanded;
    if (!text) return null;
    return (
        <button
            type="button"
            data-testid="messagerie-thread-group-description"
            onClick={() => setExpanded((v) => !v)}
            className="block text-left text-[11px] text-[#6B7280] max-w-[380px] hover:text-[#4B3FD8]"
            title={expanded ? 'Réduire' : 'Voir la description complète'}
        >
            {short ? `${text.slice(0, 60)}…` : text}
        </button>
    );
}

// -----------------------------------------------------------------------
// C5 — Composer avec Enter/Shift+Enter + preview reply.
// -----------------------------------------------------------------------
function MessageComposer({
    thread, draft, setDraft, onSend, sending,
    attachedFiles, setAttachedFiles, uploadState,
    replyTo, onClearReply,
}) {
    const conv = thread?.conversation || {};
    const isWhapi = !!conv.channel_id;
    const composerBadge = isWhapi ? conv.channel_id : null;
    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef(null);
    const textareaRef = React.useRef(null);

    const addFiles = (fileList) => {
        const arr = Array.from(fileList || []);
        if (!arr.length) return;
        setAttachedFiles((prev) => [...prev, ...arr.filter((f) => f && f.size > 0)]);
    };
    const removeFile = (idx) => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

    const onPick = (e) => {
        addFiles(e.target.files);
        e.target.value = '';
    };
    const onDragOver = (e) => {
        if (!isWhapi) return;
        e.preventDefault(); e.stopPropagation();
        setDragging(true);
    };
    const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
    const onDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragging(false);
        if (!isWhapi) return;
        addFiles(e.dataTransfer && e.dataTransfer.files);
    };

    const canSubmit = !sending && ((draft.trim().length > 0) || attachedFiles.length > 0);

    // C5 — Enter = envoyer, Shift+Enter = nouvelle ligne.
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey && !e.isComposing) {
            e.preventDefault();
            if (canSubmit) onSend();
        }
    };

    // Auto-resize textarea (max 6 lignes ≈ 144px).
    React.useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const h = Math.min(el.scrollHeight, 144);
        el.style.height = h + 'px';
    }, [draft]);

    // Focus sur le composer dès qu'on cite un message.
    React.useEffect(() => {
        if (replyTo && textareaRef.current) textareaRef.current.focus();
    }, [replyTo]);

    return (
        <form
            data-testid="messagerie-composer"
            data-whapi={isWhapi ? 'true' : 'false'}
            onSubmit={(e) => { e.preventDefault(); onSend(); }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={
                'shrink-0 border-t border-[#E5E7EB] bg-white p-3 space-y-2 relative ' +
                (dragging ? 'ring-2 ring-[#4B3FD8] ring-offset-0' : '')
            }
        >
            {dragging ? (
                <div
                    data-testid="messagerie-composer-dropzone"
                    className="absolute inset-0 bg-[#4B3FD8]/8 flex items-center justify-center text-sm text-[#4B3FD8] font-medium pointer-events-none rounded"
                >
                    Déposez vos fichiers pour les envoyer
                </div>
            ) : null}

            {replyTo ? (
                <div
                    data-testid="messagerie-composer-reply-preview"
                    className="flex items-start gap-2 pl-2 py-1.5 pr-1 border-l-2 border-[#4B3FD8] bg-[#4B3FD8]/5 rounded-r-md"
                >
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-[#4B3FD8]">
                            Réponse à {replyTo.direction === 'outbound' ? 'Vous' : (replyTo.sender_display_name || 'Contact')}
                        </div>
                        <div className="text-[11px] text-[#6B7280] truncate">
                            {replyTo.body
                                ? (replyTo.body.length > 80 ? replyTo.body.slice(0, 80) + '…' : replyTo.body)
                                : (replyTo.has_media ? `[${replyTo.media?.kind || 'Média'}]` : '[Message]')}
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="messagerie-composer-reply-cancel"
                        onClick={onClearReply}
                        className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded text-[#6B7280] hover:bg-white hover:text-[#EF4444]"
                        aria-label="Annuler la réponse"
                    >
                        <CloseIcon size={14} />
                    </button>
                </div>
            ) : null}

            {attachedFiles.length > 0 ? (
                <ul
                    data-testid="messagerie-composer-attachments"
                    className="flex flex-wrap gap-2"
                >
                    {attachedFiles.map((f, i) => (
                        <li
                            key={`${f.name}-${i}`}
                            data-testid="messagerie-composer-attachment"
                            data-filename={f.name}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F5F6FA] border border-[#E5E7EB] text-xs text-[#1A1D2E]"
                        >
                            {kindFromFile(f) === 'image' ? <ImageIcon size={12} /> :
                             kindFromFile(f) === 'video' ? <VideoIcon size={12} /> :
                             kindFromFile(f) === 'voice' ? <Mic size={12} /> :
                             <FileText size={12} />}
                            <span className="max-w-[160px] truncate">{f.name}</span>
                            <span className="text-[10px] text-[#6B7280] tabular-nums">
                                {(f.size / 1024).toFixed(1)} Ko
                            </span>
                            <button
                                type="button"
                                data-testid="messagerie-composer-attachment-remove"
                                onClick={() => removeFile(i)}
                                disabled={sending}
                                className="text-[#6B7280] hover:text-[#EF4444] disabled:opacity-40"
                                aria-label="Retirer"
                            >
                                <CloseIcon size={12} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {uploadState ? (
                <div
                    data-testid="messagerie-composer-progress"
                    data-percent={uploadState.percent}
                    className="flex items-center gap-2 text-[11px] text-[#6B7280]"
                >
                    <span className="tabular-nums shrink-0">
                        {uploadState.index + 1}/{uploadState.total}
                    </span>
                    <div className="h-1.5 flex-1 rounded-full bg-[#F3F4F6] overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] transition-all"
                            style={{ width: `${uploadState.percent}%` }}
                        />
                    </div>
                    <span className="tabular-nums shrink-0">{uploadState.percent}%</span>
                </div>
            ) : null}
            <div className="flex items-end gap-2">
                {composerBadge ? (
                    <span
                        data-testid="messagerie-composer-channel-badge"
                        data-channel-id={composerBadge}
                        className="inline-flex items-center h-9 px-2 rounded text-[10px] font-medium border border-[#4B3FD8] text-[#4B3FD8] bg-[#4B3FD8]/5"
                        title="Canal d'envoi"
                    >
                        {composerBadge}
                    </span>
                ) : null}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    data-testid="messagerie-composer-file-input"
                    onChange={onPick}
                    disabled={!isWhapi || sending}
                />
                <button
                    type="button"
                    data-testid="messagerie-composer-attach"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={!isWhapi || sending}
                    title={isWhapi
                        ? 'Joindre un fichier (image, audio, vidéo, document)'
                        : 'Les médias ne sont pas supportés sur cette conversation.'}
                    aria-label="Joindre un fichier"
                    className="h-9 w-9 shrink-0 rounded-lg border border-[#E5E7EB] bg-white text-[#4B3FD8] inline-flex items-center justify-center hover:bg-[#F5F6FA] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Paperclip size={16} strokeWidth={1.75} />
                </button>
                <textarea
                    ref={textareaRef}
                    data-testid="messagerie-composer-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={attachedFiles.length ? 'Ajouter un message (facultatif)…' : 'Écrivez un message… (Shift+Entrée = nouvelle ligne)'}
                    disabled={sending}
                    rows={1}
                    className="flex-1 min-h-9 max-h-36 py-2 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8] disabled:opacity-60 resize-none leading-5"
                />
                <button
                    type="submit"
                    data-testid="messagerie-composer-send"
                    disabled={!canSubmit}
                    className="h-9 px-3 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0"
                >
                    <Send size={14} strokeWidth={2} />
                    Envoyer
                </button>
            </div>
        </form>
    );
}

// -----------------------------------------------------------------------
// C3 — MessageStream: wallpaper, bulles, séparateurs, marqueur unread,
//      backfill lazy, bouton "↓ Nouveaux messages".
// -----------------------------------------------------------------------
function MessageStream({ messages, loading, onRetry, onReact, onReply, isGroup, conversation, currentUser, openLightbox, unreadSnapshot }) {
    const isSuperAdmin = !!(currentUser && currentUser.role === ROLES.SUPER_ADMIN);
    const scrollerRef = React.useRef(null);
    const [pinnedBottom, setPinnedBottom] = React.useState(true);
    const [hasNew, setHasNew] = React.useState(false);
    const lastCountRef = React.useRef(messages.length);
    const [backfilledItems, setBackfilledItems] = React.useState([]);
    const [hasMore, setHasMore] = React.useState(true);
    const [backfilling, setBackfilling] = React.useState(false);
    const convId = conversation?.id;
    React.useEffect(() => {
        setBackfilledItems([]);
        setHasMore(true);
        setBackfilling(false);
    }, [convId]);

    const allMessages = React.useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const m of [...backfilledItems, ...messages]) {
            if (!m || seen.has(m.id)) continue;
            seen.add(m.id);
            result.push(m);
        }
        return result;
    }, [backfilledItems, messages]);

    const whapiIdMap = React.useMemo(() => {
        const m = new Map();
        for (const msg of allMessages) {
            if (msg.whapi_message_id) m.set(msg.whapi_message_id, msg);
            if (msg.external_message_id && !m.has(msg.external_message_id)) {
                m.set(msg.external_message_id, msg);
            }
        }
        return m;
    }, [allMessages]);

    const scrollToBottom = React.useCallback((behavior = 'smooth') => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
        setPinnedBottom(true);
        setHasNew(false);
    }, []);

    React.useEffect(() => {
        scrollToBottom('auto');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (messages.length && lastCountRef.current === 0) {
            scrollToBottom('auto');
        }
        if (messages.length > lastCountRef.current) {
            if (pinnedBottom) scrollToBottom('smooth');
            else setHasNew(true);
        }
        lastCountRef.current = messages.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    const onScroll = (e) => {
        const el = e.currentTarget;
        const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const atBottom = dist < 80;
        setPinnedBottom(atBottom);
        if (atBottom) setHasNew(false);
        if (el.scrollTop < 100 && hasMore && !backfilling
            && convId && allMessages.length > 0) {
            const oldest = allMessages[0];
            const beforeTs = oldest?.timestamp
                ? Math.floor(new Date(oldest.timestamp).getTime() / 1000)
                : null;
            if (!beforeTs) return;
            setBackfilling(true);
            const prevHeight = el.scrollHeight;
            messagerieApi.backfill(convId, beforeTs)
                .then((res) => {
                    if (res && Array.isArray(res.items) && res.items.length) {
                        setBackfilledItems((prev) => [...res.items, ...prev]);
                        requestAnimationFrame(() => {
                            const node = scrollerRef.current;
                            if (node) {
                                const newHeight = node.scrollHeight;
                                node.scrollTop = newHeight - prevHeight + node.scrollTop;
                            }
                        });
                    }
                    setHasMore(res && res.has_more === true);
                })
                .catch(() => { setHasMore(false); })
                .finally(() => setBackfilling(false));
        }
    };

    // C4 — regroupement album avant insertion des séparateurs.
    const grouped = React.useMemo(() => buildAlbums(allMessages), [allMessages]);

    const nowIso = new Date().toISOString();
    const rendered = [];
    let lastDayKey = null;
    let lastSenderKey = null;
    // C3 — ligne "X messages non lus" : positionnée AVANT le 1er message
    // inbound NON lu (approximation : à partir du (N - unreadSnapshot) dernier
    // message, pour les inbound). On n'affiche la ligne qu'une seule fois.
    let unreadMarkerShown = false;
    // Indices des messages inbound (pour la comparaison avec unreadSnapshot).
    const inboundIndices = [];
    for (let i = 0; i < grouped.length; i++) {
        const g = grouped[i];
        if (g && !g.__type && g.direction === 'inbound') inboundIndices.push(i);
        if (g && g.__type === 'album' && g.items[0]?.direction === 'inbound') inboundIndices.push(i);
    }
    const firstUnreadIndex = (unreadSnapshot > 0 && inboundIndices.length >= unreadSnapshot)
        ? inboundIndices[inboundIndices.length - unreadSnapshot]
        : -1;

    for (let i = 0; i < grouped.length; i++) {
        const g = grouped[i];
        const refMsg = g.__type === 'album' ? g.items[0] : g;
        const dayKey = casaDayKey(refMsg.timestamp);
        if (dayKey && dayKey !== lastDayKey) {
            rendered.push(
                <DateSeparator key={`sep-${dayKey}-${i}`} label={formatSeparatorLabel(refMsg.timestamp, nowIso)} />,
            );
            lastDayKey = dayKey;
            lastSenderKey = null;
        }
        if (!unreadMarkerShown && firstUnreadIndex === i) {
            rendered.push(<UnreadSeparator key={`unread-${i}`} count={unreadSnapshot} />);
            unreadMarkerShown = true;
        }
        if (g.__type === 'album') {
            // Album → AlbumBubble
            const first = g.items[0];
            const isInbound = first.direction === 'inbound';
            const showSender = isGroup && isInbound && (first.sender_wa_id || first.from_phone || first.from);
            const currentSenderKey = first.sender_wa_id || first.from_phone || first.from || null;
            const senderChanged = showSender && currentSenderKey !== lastSenderKey;
            rendered.push(
                <AlbumBubble
                    key={g.items.map((x) => x.id).join('-')}
                    items={g.items}
                    anchor={g.anchor}
                    isGroup={isGroup}
                    showSender={senderChanged}
                    onReact={onReact}
                    onReply={onReply}
                    onRetry={onRetry}
                    openLightbox={openLightbox}
                    conversation={conversation}
                    isSuperAdmin={isSuperAdmin}
                />,
            );
            if (isInbound) lastSenderKey = currentSenderKey;
            else lastSenderKey = null;
            continue;
        }
        const m = g;
        if (m.direction === 'system' || m.message_type === 'system') {
            rendered.push(<SystemMessage key={m.id} message={m} />);
            lastSenderKey = null;
            continue;
        }
        const isInbound = m.direction === 'inbound';
        const showSender = isGroup && isInbound && (m.sender_wa_id || m.from_phone || m.from);
        const currentSenderKey = m.sender_wa_id || m.from_phone || m.from || null;
        const senderChanged = showSender && currentSenderKey !== lastSenderKey;
        rendered.push(
            <MessageBubble
                key={m.id}
                message={m}
                onRetry={onRetry}
                onReact={onReact}
                onReply={onReply}
                isGroup={isGroup}
                showSender={senderChanged}
                repliedTo={m.reply_to_whapi_id ? (whapiIdMap.get(m.reply_to_whapi_id) || null) : null}
                conversation={conversation}
                isSuperAdmin={isSuperAdmin}
                openLightbox={openLightbox}
            />,
        );
        if (isInbound) lastSenderKey = currentSenderKey;
        else lastSenderKey = null;
    }

    return (
        <div className="relative flex-1 min-h-0">
            <div
                ref={scrollerRef}
                data-testid="messagerie-thread-scroll"
                onScroll={onScroll}
                className="absolute inset-0 overflow-y-auto px-4 py-4 space-y-1.5"
                style={{
                    backgroundColor: '#F5F6FA',
                    backgroundImage: WHATSAPP_WALLPAPER,
                    backgroundSize: '120px 120px',
                    backgroundRepeat: 'repeat',
                }}
            >
                {backfilling ? (
                    <div
                        data-testid="messagerie-backfill-spinner"
                        className="text-center text-xs text-[#6B7280] py-2"
                    >
                        Chargement des messages plus anciens…
                    </div>
                ) : (!hasMore && allMessages.length > 0) ? (
                    <div
                        data-testid="messagerie-thread-start"
                        className="text-center text-xs text-[#9CA3AF] py-2 italic"
                    >
                        — Début de la conversation —
                    </div>
                ) : null}
                {loading && allMessages.length === 0 ? (
                    <div className="text-center text-xs text-[#6B7280]">Chargement…</div>
                ) : null}
                {!loading && allMessages.length === 0 ? (
                    <div className="text-center text-xs text-[#6B7280]">Aucun message dans cette conversation.</div>
                ) : null}
                {rendered}
            </div>
            {hasNew ? (
                <button
                    type="button"
                    data-testid="messagerie-new-messages-btn"
                    onClick={() => scrollToBottom('smooth')}
                    className="absolute left-1/2 -translate-x-1/2 bottom-3 h-8 px-3.5 rounded-full bg-[#4B3FD8] text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-lg hover:bg-[#3a2fb8]"
                >
                    <ArrowDown size={12} strokeWidth={2.5} />
                    Nouveaux messages
                </button>
            ) : null}
        </div>
    );
}

function SystemMessage({ message }) {
    return (
        <div
            data-testid="messagerie-msg-system"
            data-system-event={message.system_event_type || 'unknown'}
            className="my-2 flex items-center justify-center"
        >
            <span className="px-3 py-1 rounded-full bg-white/80 border border-[#E5E7EB] text-[11px] font-medium text-[#6B7280] shadow-sm max-w-[80%] text-center backdrop-blur-sm">
                {message.body || '…'}
            </span>
        </div>
    );
}

function DateSeparator({ label }) {
    return (
        <div
            data-testid="messagerie-date-separator"
            data-label={label}
            className="my-3 flex items-center justify-center"
        >
            <span className="px-2.5 py-1 rounded-md bg-white/90 border border-[#E5E7EB] text-[10px] font-medium text-[#6B7280] shadow-sm backdrop-blur-sm">
                {label}
            </span>
        </div>
    );
}

// C3 — ligne "X messages non lus" : trait fin + badge centré.
function UnreadSeparator({ count }) {
    const label = count > 1 ? `${count} messages non lus` : '1 message non lu';
    return (
        <div
            data-testid="messagerie-unread-separator"
            data-count={count}
            className="my-3 flex items-center justify-center gap-2"
        >
            <div className="flex-1 h-px bg-[#4B3FD8]/20" />
            <span className="px-2.5 py-0.5 rounded-full bg-[#4B3FD8]/10 border border-[#4B3FD8]/30 text-[10px] font-semibold text-[#4B3FD8]">
                {label}
            </span>
            <div className="flex-1 h-px bg-[#4B3FD8]/20" />
        </div>
    );
}

function MessageBody({ text }) {
    const parts = React.useMemo(() => parseWhatsAppMarkdown(text || ''), [text]);
    if (parts.length === 0) return null;
    return (
        <Linkify as="span" options={LINKIFY_OPTS}>
            {parts.map((p, i) => {
                if (p.type === 'bold') return <strong key={i}>{p.text}</strong>;
                if (p.type === 'italic') return <em key={i}>{p.text}</em>;
                if (p.type === 'strike') return <s key={i}>{p.text}</s>;
                return <React.Fragment key={i}>{p.text}</React.Fragment>;
            })}
        </Linkify>
    );
}

function OutboundStatusIcon({ status, onRetry }) {
    if (status === 'sending' || !status) {
        return (
            <span
                data-testid="messagerie-msg-status"
                data-status="sending"
                title="Envoi en cours…"
                aria-label="Envoi en cours"
                className="inline-flex items-center text-[#6B7280]"
            >
                <Clock size={12} strokeWidth={2} />
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <button
                type="button"
                data-testid="messagerie-msg-retry"
                data-status="failed"
                onClick={onRetry}
                title="Échec — cliquez pour réessayer"
                aria-label="Échec — cliquez pour réessayer"
                className="inline-flex items-center gap-0.5 text-[#EF4444] hover:opacity-80"
            >
                <AlertTriangle size={12} strokeWidth={2} />
                <RotateCw size={10} strokeWidth={2} />
            </button>
        );
    }
    if (status === 'delivered') {
        return (
            <span data-testid="messagerie-msg-status" data-status="delivered" title="Distribué" aria-label="Distribué" className="inline-flex items-center text-[#6B7280]">
                <CheckCheck size={12} strokeWidth={2} />
            </span>
        );
    }
    if (status === 'read') {
        return (
            <span data-testid="messagerie-msg-status" data-status="read" title="Lu" aria-label="Lu" className="inline-flex items-center text-[#3B82F6]">
                <CheckCheck size={12} strokeWidth={2} />
            </span>
        );
    }
    return (
        <span data-testid="messagerie-msg-status" data-status="sent" title="Envoyé" aria-label="Envoyé" className="inline-flex items-center text-[#6B7280]">
            <Check size={12} strokeWidth={2} />
        </span>
    );
}

// -----------------------------------------------------------------------
// C4 — Cartes natives (location, contact, link preview) à partir du
// `rich_content` persisté par le worker. Fallback = body texte.
// -----------------------------------------------------------------------
function LocationCard({ data }) {
    const { latitude, longitude, name, address, live } = data || {};
    const mapsUrl = (latitude != null && longitude != null)
        ? `https://maps.google.com/?q=${latitude},${longitude}` : null;
    const staticMap = (latitude != null && longitude != null)
        ? `https://staticmap.openstreetmap.de/staticmap.php?center=${latitude},${longitude}&zoom=14&size=260x120&markers=${latitude},${longitude},red-pushpin`
        : null;
    return (
        <a
            href={mapsUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="messagerie-rich-location"
            data-live={live ? 'true' : 'false'}
            className="block max-w-[280px] rounded-lg overflow-hidden border border-[#E5E7EB] bg-white hover:border-[#4B3FD8]/50"
        >
            {staticMap ? (
                <img
                    src={staticMap}
                    alt="Carte"
                    loading="lazy"
                    className="w-full h-[120px] object-cover bg-[#F5F6FA]"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            ) : null}
            <div className="p-2 flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-[#4B3FD8]/10 flex items-center justify-center text-[#4B3FD8] shrink-0">
                    <MapPin size={14} strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#1A1D2E] truncate">
                        {name || (live ? 'Localisation en direct' : 'Localisation')}
                    </div>
                    {address ? (
                        <div className="text-[11px] text-[#6B7280] truncate">{address}</div>
                    ) : null}
                    {latitude != null && longitude != null ? (
                        <div className="text-[10px] text-[#4B3FD8] mt-0.5 font-medium">
                            Ouvrir dans Maps →
                        </div>
                    ) : null}
                </div>
            </div>
        </a>
    );
}

function ContactCard({ data }) {
    const contacts = data?.contacts || [];
    if (!contacts.length) return null;
    return (
        <div data-testid="messagerie-rich-contact" className="space-y-1.5 max-w-[280px]">
            {contacts.map((c, i) => (
                <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB] bg-white"
                >
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-[11px] shrink-0"
                        style={{ backgroundColor: avatarColorFor(c.name || c.phones?.[0] || String(i)) }}
                    >
                        {initialsFrom(c.name, c.phones?.[0])}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-[#1A1D2E] truncate">{c.name || '(sans nom)'}</div>
                        {(c.phones || []).map((p, j) => (
                            <a
                                key={j}
                                href={`tel:${p.replace(/\s/g, '')}`}
                                className="block text-[11px] text-[#4B3FD8] truncate underline-offset-2 hover:underline"
                            >
                                <Phone size={10} strokeWidth={2} className="inline mr-1 -mt-0.5" />
                                {p}
                            </a>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function LinkPreviewCard({ data, bodyFallback }) {
    const { url, title, description, image_url } = data || {};
    const href = url || '#';
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="messagerie-rich-link-preview"
            className="block max-w-[280px] rounded-lg overflow-hidden border border-[#E5E7EB] bg-white hover:border-[#4B3FD8]/50"
        >
            {image_url ? (
                <img
                    src={image_url}
                    alt={title || url}
                    loading="lazy"
                    className="w-full h-[120px] object-cover bg-[#F5F6FA]"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            ) : null}
            <div className="p-2 flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-[#4B3FD8]/10 flex items-center justify-center text-[#4B3FD8] shrink-0">
                    <LinkIcon size={13} strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                    {title ? (
                        <div className="text-xs font-semibold text-[#1A1D2E] line-clamp-2">{title}</div>
                    ) : null}
                    {description && description !== title ? (
                        <div className="text-[11px] text-[#6B7280] line-clamp-2 mt-0.5">{description}</div>
                    ) : null}
                    <div className="text-[10px] text-[#4B3FD8] mt-0.5 truncate font-medium">{url || bodyFallback}</div>
                </div>
            </div>
        </a>
    );
}

// -----------------------------------------------------------------------
// C4 — MediaAttachment + AudioPlayer amélioré.
// -----------------------------------------------------------------------
function MediaAttachment({ media, onClick }) {
    if (!media) return null;
    if (media.status === 'expired') {
        return (
            <span
                data-testid="messagerie-media-expired"
                title="Ce média a expiré et a été supprimé du stockage."
                className="inline-flex items-center gap-1.5 italic text-[#9CA3AF] text-xs px-2 py-1 rounded bg-[#F5F6FA] border border-dashed border-[#E5E7EB]"
            >
                <FileText size={12} strokeWidth={1.75} />
                Média expiré
            </span>
        );
    }
    const key = media.r2_key;
    if (!key) {
        return <span className="italic text-[#6B7280]" data-testid="messagerie-media-placeholder">[Média]</span>;
    }
    const href = messagerieApi.mediaHref(key);
    const kind = (media.kind || '').toLowerCase();
    const filename = media.filename || (key.split('/').pop() || 'fichier');

    if (kind === 'image' || kind === 'sticker') {
        return (
            <button
                type="button"
                onClick={onClick}
                data-testid="messagerie-media-image"
                data-r2-key={key}
                className="block max-w-[280px] rounded-lg overflow-hidden"
            >
                <img
                    src={href}
                    alt={filename}
                    loading="lazy"
                    className={
                        'w-full h-auto object-cover ' +
                        (kind === 'sticker' ? 'bg-transparent max-h-[160px]' : 'bg-[#F5F6FA]')
                    }
                />
            </button>
        );
    }
    if (kind === 'video') {
        return (
            <div className="relative max-w-[280px]">
                <video
                    data-testid="messagerie-media-video"
                    data-r2-key={key}
                    src={href}
                    controls
                    preload="metadata"
                    className="w-full h-auto rounded-lg bg-black"
                />
            </div>
        );
    }
    if (kind === 'voice' || kind === 'audio') {
        return <VoiceMessage href={href} r2Key={key} duration={media.duration} kind={kind} />;
    }
    return <DocumentCard href={href} filename={filename} fileSize={media.file_size} r2Key={key} />;
}

// VoiceMessage: lecteur vocal custom (WhatsApp-like).
function VoiceMessage({ href, r2Key, duration, kind }) {
    const audioRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [current, setCurrent] = React.useState(0);
    const [dur, setDur] = React.useState(duration ? Number(duration) : 0);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
    };

    const onTime = () => {
        const a = audioRef.current;
        if (!a) return;
        setCurrent(a.currentTime);
        if (a.duration && !isNaN(a.duration)) {
            setDur(a.duration);
            setProgress((a.currentTime / a.duration) * 100);
        }
    };

    const fmt = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className="flex items-center gap-2.5 min-w-[220px] max-w-[280px] py-1"
            data-testid="messagerie-media-audio"
            data-r2-key={r2Key}
            data-kind={kind}
        >
            <button
                type="button"
                onClick={toggle}
                className="w-9 h-9 rounded-full bg-[#4B3FD8] text-white inline-flex items-center justify-center shrink-0 hover:bg-[#3a2fb8]"
                aria-label={playing ? 'Pause' : 'Lecture'}
            >
                {playing ? (
                    <span className="flex gap-0.5">
                        <span className="w-[3px] h-3 bg-white rounded-sm" />
                        <span className="w-[3px] h-3 bg-white rounded-sm" />
                    </span>
                ) : (
                    <Play size={14} strokeWidth={2.5} className="ml-0.5" />
                )}
            </button>
            <div className="flex-1 min-w-0">
                <div className="h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div
                        className="h-full bg-[#4B3FD8] transition-all duration-150"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="mt-1 text-[10px] text-[#6B7280] tabular-nums">
                    {playing || current > 0 ? fmt(current) : fmt(dur)}
                </div>
            </div>
            <audio
                ref={audioRef}
                src={href}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }}
                onTimeUpdate={onTime}
                onLoadedMetadata={onTime}
                className="hidden"
            />
        </div>
    );
}

function DocumentCard({ href, filename, fileSize, r2Key }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            download={filename}
            data-testid="messagerie-media-document"
            data-r2-key={r2Key}
            className="inline-flex items-center gap-2.5 max-w-[300px] p-2 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#4B3FD8]/50 transition-colors"
        >
            <div className="w-9 h-9 rounded-md bg-[#4B3FD8]/10 flex items-center justify-center text-[#4B3FD8] shrink-0">
                <FileText size={16} strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="truncate text-xs text-[#1A1D2E] font-medium">{filename}</div>
                {fileSize ? (
                    <div className="text-[10px] text-[#6B7280] tabular-nums">
                        {(Number(fileSize) / 1024).toFixed(1)} Ko
                    </div>
                ) : null}
            </div>
            <Download size={14} strokeWidth={1.75} className="text-[#6B7280] shrink-0" />
        </a>
    );
}

// C4 — Album: grille 2x2 avec overlay "+N" si plus de 4.
function AlbumGrid({ items, openLightbox }) {
    const maxVisible = 4;
    const visible = items.slice(0, maxVisible);
    const extra = Math.max(0, items.length - maxVisible);
    const cols = items.length === 1 ? 1 : 2;
    return (
        <div
            data-testid="messagerie-media-album"
            data-count={items.length}
            className={
                'grid gap-0.5 max-w-[280px] rounded-lg overflow-hidden ' +
                (cols === 2 ? 'grid-cols-2' : 'grid-cols-1')
            }
        >
            {visible.map((m, i) => {
                const media = m.media;
                const key = media?.r2_key;
                const href = key ? messagerieApi.mediaHref(key) : null;
                const kind = String(media?.kind || '').toLowerCase();
                const isLastWithExtra = i === maxVisible - 1 && extra > 0;
                return (
                    <button
                        key={m.id}
                        type="button"
                        onClick={() => openLightbox(items.map((it) => it.media).filter(Boolean), i)}
                        data-testid="messagerie-album-cell"
                        data-r2-key={key || ''}
                        className="relative aspect-square overflow-hidden bg-[#F5F6FA] hover:opacity-90"
                    >
                        {href ? (
                            kind === 'video' ? (
                                <>
                                    <video src={href} className="w-full h-full object-cover" preload="metadata" muted />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
                                            <Play size={18} strokeWidth={2.5} className="text-white ml-0.5" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <img src={href} alt="" loading="lazy" className="w-full h-full object-cover" />
                            )
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#9CA3AF] text-[10px]">
                                [Média]
                            </div>
                        )}
                        {isLastWithExtra ? (
                            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                                <span className="text-white text-lg font-semibold tabular-nums">+{extra}</span>
                            </div>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

// -----------------------------------------------------------------------
// C5 — Hover menu (émoji + reply). Flotte sur les bulles.
// -----------------------------------------------------------------------
function HoverMenu({ message, isOut, onReact, onReply }) {
    // Ordre : émoji, répondre. Flip pour outbound (menu à gauche de la bulle).
    return (
        <div
            data-testid="messagerie-msg-hover-menu"
            className={
                'absolute top-1 opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-0.5 bg-white border border-[#E5E7EB] rounded-full p-0.5 shadow-md z-10 ' +
                (isOut ? 'right-full mr-1' : 'left-full ml-1')
            }
        >
            <ReactionPicker onReact={onReact} message={message} />
            {onReply ? (
                <button
                    type="button"
                    onClick={() => onReply(message)}
                    data-testid="messagerie-msg-reply-btn"
                    title="Répondre"
                    aria-label="Répondre"
                    className="w-6 h-6 inline-flex items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F5F6FA] hover:text-[#4B3FD8]"
                >
                    <Reply size={13} strokeWidth={2} />
                </button>
            ) : null}
        </div>
    );
}

// -----------------------------------------------------------------------
// Overlapping reaction pills (position: absolute pour chevaucher la bulle).
// -----------------------------------------------------------------------
function ReactionPills({ reactions, align, overlap }) {
    const groups = React.useMemo(() => {
        const m = new Map();
        for (const r of reactions || []) {
            if (!r || !r.emoji) continue;
            const key = r.emoji;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(r);
        }
        return Array.from(m.entries());
    }, [reactions]);
    if (!groups.length) return null;
    return (
        <div
            data-testid="messagerie-msg-reactions"
            className={
                'flex flex-wrap gap-1 pointer-events-auto ' +
                (overlap ? 'absolute -bottom-2.5 z-10 ' : 'mt-1 ') +
                (align === 'end' ? (overlap ? 'right-2 justify-end' : 'justify-end') : (overlap ? 'left-2 justify-start' : 'justify-start'))
            }
        >
            {groups.map(([emoji, list]) => {
                const authors = list.map((r) => r.from_name || r.from_phone || 'Contact').join(', ');
                const isMine = list.some((r) => r && r.from_me === true);
                return (
                    <span
                        key={emoji}
                        data-mine={isMine ? 'true' : 'false'}
                        title={authors}
                        className={
                            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] shadow-sm ' +
                            (isMine
                                ? 'bg-[#EEF0FE] border border-[#4B3FD8] text-[#1A1D2E]'
                                : 'bg-white border border-[#E5E7EB] text-[#1A1D2E]')
                        }
                    >
                        <span>{emoji}</span>
                        {list.length > 1 ? (
                            <span className="text-[#6B7280] tabular-nums text-[10px]">{list.length}</span>
                        ) : null}
                    </span>
                );
            })}
        </div>
    );
}

const EmojiPickerLazy = React.lazy(() => import('emoji-picker-react'));
const PICKER_W = 320;
const PICKER_H = 400;
const PICKER_MARGIN = 8;

function ReactionPicker({ onReact, message }) {
    const [open, setOpen] = React.useState(false);
    const [coords, setCoords] = React.useState(null);
    const triggerRef = React.useRef(null);
    const popupRef = React.useRef(null);

    const computeCoords = React.useCallback(() => {
        if (!triggerRef.current) return null;
        const rect = triggerRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let left = rect.left;
        let top = rect.bottom + PICKER_MARGIN;
        if (left + PICKER_W + PICKER_MARGIN > vw) {
            left = Math.max(PICKER_MARGIN, rect.right - PICKER_W);
        }
        if (top + PICKER_H + PICKER_MARGIN > vh) {
            top = Math.max(PICKER_MARGIN, rect.top - PICKER_H - PICKER_MARGIN);
        }
        return { left: Math.max(PICKER_MARGIN, left), top: Math.max(PICKER_MARGIN, top) };
    }, []);

    React.useEffect(() => {
        if (!open) return;
        setCoords(computeCoords());
        const onDocClick = (e) => {
            if (popupRef.current && popupRef.current.contains(e.target)) return;
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onScroll = () => setCoords(computeCoords());
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onScroll);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onScroll);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open, computeCoords]);

    const choose = (emoji) => {
        setOpen(false);
        onReact && onReact(message, emoji);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                data-testid="messagerie-msg-react-trigger"
                aria-label="Ajouter une r\u00e9action"
                onClick={() => setOpen((v) => !v)}
                className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[#6B7280] hover:bg-[#F5F6FA] hover:text-[#4B3FD8]"
            >
                <Smile size={13} strokeWidth={2} />
            </button>
            {open && coords ? createPortal(
                <div
                    ref={popupRef}
                    data-testid="messagerie-msg-react-popup"
                    style={{ position: 'fixed', left: coords.left, top: coords.top, zIndex: 9999 }}
                >
                    <React.Suspense fallback={
                        <div className="px-3 py-2 rounded-md bg-white border border-[#E5E7EB] shadow-md text-xs text-[#6B7280]">
                            Chargement\u2026
                        </div>
                    }>
                        <EmojiPickerLazy
                            onEmojiClick={(data) => choose(data.emoji)}
                            autoFocusSearch={false}
                            searchPlaceHolder="Rechercher"
                            previewConfig={{ showPreview: false }}
                            lazyLoadEmojis
                            height={PICKER_H}
                            width={PICKER_W}
                        />
                    </React.Suspense>
                </div>,
                document.body,
            ) : null}
        </>
    );
}

// Phase A.2 — Bloc de citation (reply-to).
function QuotedPreview({ repliedTo, conversation }) {
    if (!repliedTo) {
        return (
            <div
                data-testid="messagerie-msg-quote"
                data-quote-resolved="false"
                className="mb-1 pl-2 py-1 border-l-2 border-[#4B3FD8] text-[11px] italic text-[#9CA3AF] bg-black/[0.03] rounded-sm"
            >
                Message référencé
            </div>
        );
    }
    const quotedIsOut = repliedTo.direction === 'outbound';
    const senderLabel = quotedIsOut
        ? 'Vous'
        : (repliedTo.sender_display_name || (conversation && conversation.contact_name) || 'Contact');
    let preview;
    if (repliedTo.body && repliedTo.body.length) {
        preview = repliedTo.body.slice(0, 60);
        if (repliedTo.body.length > 60) preview += '…';
    } else if (repliedTo.has_media && repliedTo.media) {
        const kind = (repliedTo.media.kind || repliedTo.media.type || '').toLowerCase();
        if (kind.startsWith('image')) preview = '[Image]';
        else if (kind.startsWith('audio') || kind === 'voice' || kind === 'ptt') preview = '[Audio]';
        else if (kind.startsWith('video')) preview = '[Vidéo]';
        else preview = '[Document]';
    } else {
        preview = '[Message]';
    }
    return (
        <div
            data-testid="messagerie-msg-quote"
            data-quote-resolved="true"
            className="mb-1 pl-2 py-1 border-l-2 border-[#4B3FD8] bg-black/[0.03] rounded-sm"
        >
            <div className="text-[11px] font-semibold text-[#4B3FD8] truncate">
                {senderLabel}
            </div>
            <div className="text-[12px] text-[#6B7280] truncate">
                {preview}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// C3/C4 — AlbumBubble (groupe de médias).
// -----------------------------------------------------------------------
function AlbumBubble({ items, anchor, isGroup, showSender, onReact, onReply, onRetry, openLightbox, conversation, isSuperAdmin }) {
    const first = items[0];
    const isOut = first.direction === 'outbound';
    const isDeleted = first.deleted === true;
    const senderName = first.sender_display_name
        || (first.sender_phone && `+${first.sender_phone}`)
        || (first.from_phone && `+${first.from_phone}`)
        || 'Participant';
    const senderKey = first.sender_wa_id || first.from_phone || first.from || senderName;
    // Caption : première caption non vide parmi les items.
    const caption = items.map((m) => m.body).find((b) => b && b.length) || '';
    const lastItem = items[items.length - 1];
    const status = lastItem.status || (isOut ? 'sent' : null);
    const allReactions = items.reduce((acc, m) => acc.concat(m.reactions || []), []);

    return (
        <div
            data-testid="messagerie-msg-album"
            data-direction={first.direction}
            data-count={items.length}
            className={'flex gap-2 ' + (isOut ? 'justify-end' : 'justify-start')}
        >
            {isGroup && !isOut && showSender ? (
                <div className="mt-1">
                    <Avatar
                        name={senderName}
                        waId={senderKey}
                        sizeClass="w-7 h-7"
                        textClass="text-[10px]"
                        testId="messagerie-msg-sender-avatar"
                    />
                </div>
            ) : (isGroup && !isOut) ? (
                <div className="w-7 shrink-0" aria-hidden="true" />
            ) : null}
            <div className="flex flex-col max-w-[70%] group/bubble relative">
                {isGroup && !isOut && showSender ? (
                    <span
                        data-testid="messagerie-msg-sender-name"
                        data-sender-key={senderKey}
                        className="ml-2 mb-0.5 text-[11px] font-semibold"
                        style={{ color: avatarColorFor(senderKey) }}
                    >
                        {senderName}
                    </span>
                ) : null}
                <div className="relative">
                    <div
                        className={
                            'p-1 relative overflow-visible ' +
                            'rounded-2xl shadow-sm ' +
                            (isOut
                                ? 'bg-[' + OUTBOUND_BUBBLE + '] rounded-br-md'
                                : 'bg-white border border-[#E5E7EB] rounded-bl-md')
                        }
                        style={{ backgroundColor: isOut ? OUTBOUND_BUBBLE : INBOUND_BUBBLE }}
                    >
                        {isDeleted ? (
                            <div className="px-3 py-2 italic text-[#6B7280] text-sm">
                                🚫 Album supprimé
                            </div>
                        ) : (
                            <>
                                <AlbumGrid items={items} openLightbox={openLightbox} />
                                {caption ? (
                                    <div className="px-2 pt-1.5 pb-0.5 text-sm text-[#1A1D2E] whitespace-pre-wrap break-words">
                                        <MessageBody text={caption} />
                                    </div>
                                ) : null}
                            </>
                        )}
                        <div
                            className={
                                'px-2 pb-1 pt-0.5 text-[10px] tabular-nums inline-flex items-center gap-1 justify-end w-full ' +
                                (isOut ? 'text-[#4E7A2E]' : 'text-[#6B7280]')
                            }
                        >
                            <span>{casaTime(lastItem.timestamp)}</span>
                            {isOut && <OutboundStatusIcon status={status} onRetry={() => onRetry && onRetry(lastItem)} />}
                        </div>
                        {allReactions.length > 0 ? (
                            <ReactionPills reactions={allReactions} align={isOut ? 'end' : 'start'} overlap />
                        ) : null}
                    </div>
                    {!isDeleted ? (
                        <HoverMenu message={anchor} isOut={isOut} onReact={onReact} onReply={onReply} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// C3 — MessageBubble (WhatsApp-style).
// -----------------------------------------------------------------------
function MessageBubble({ message, onRetry, onReact, onReply, isGroup, showSender, repliedTo, conversation, isSuperAdmin, openLightbox }) {
    const isOut = message.direction === 'outbound';
    const isDeleted = message.deleted === true;
    const isEdited = message.edited === true;
    const hasReactionError = !!message.reaction_send_error;
    const [showOriginal, setShowOriginal] = React.useState(false);
    const canReveal = isDeleted && isSuperAdmin && !!(message.body || message.has_media);
    const revealed = isDeleted && showOriginal && canReveal;
    const hasBody = (!isDeleted || revealed) && !!(message.body && message.body.length);
    const hasMedia = (!isDeleted || revealed) && !!(message.has_media && message.media);
    const hasR2Media = !!(hasMedia && message.media.r2_key);
    const status = message.status || (isOut ? 'sent' : null);
    const handleRetry = () => onRetry && onRetry(message);
    const hasReplyRef = (!isDeleted || revealed) && !!message.reply_to_whapi_id;
    const senderName = message.sender_display_name
        || (message.sender_phone && `+${message.sender_phone}`)
        || (message.from_phone && `+${message.from_phone}`)
        || (message.from && message.from.split('@')[0])
        || 'Participant';
    const senderKey = message.sender_wa_id || message.from_phone || message.from || senderName;
    // Rich content (location / contact / link preview).
    const rich = (!isDeleted || revealed) ? (message.rich_content || null) : null;
    const hasReactions = !isDeleted && Array.isArray(message.reactions) && message.reactions.length > 0;

    // Classe de la bulle. Note: on garde la queue (rounded-br-md / rounded-bl-md)
    // pour imiter WhatsApp. Pour les stickers-only, on utilise un fond
    // transparent (comportement natif) mais on garde la bulle pour que
    // le timestamp + status soient visibles sous l'image.
    const kindLower = String(message.media?.kind || '').toLowerCase();
    const isStickerOnly = hasMedia && !hasBody && kindLower === 'sticker' && !rich;
    const bubbleBg = isStickerOnly
        ? 'transparent'
        : (isOut ? OUTBOUND_BUBBLE : INBOUND_BUBBLE);
    const bubbleBorder = isStickerOnly || isOut
        ? ''
        : 'border border-[#E5E7EB]';

    return (
        <div
            data-testid="messagerie-msg"
            data-direction={message.direction}
            data-status={isOut ? status : undefined}
            data-has-media={hasMedia ? 'true' : 'false'}
            data-media-kind={hasR2Media ? (message.media.kind || '') : undefined}
            data-deleted={isDeleted ? 'true' : undefined}
            data-edited={isEdited ? 'true' : undefined}
            className={'flex gap-2 ' + (isOut ? 'justify-end' : 'justify-start')}
        >
            {isGroup && !isOut && showSender ? (
                <div className="mt-4">
                    <Avatar
                        name={senderName}
                        waId={senderKey}
                        sizeClass="w-7 h-7"
                        textClass="text-[10px]"
                        testId="messagerie-msg-sender-avatar"
                    />
                </div>
            ) : (isGroup && !isOut) ? (
                <div className="w-7 shrink-0" aria-hidden="true" />
            ) : null}
            <div className="flex flex-col max-w-[72%] group/bubble relative">
                {isGroup && !isOut && showSender ? (
                    <span
                        data-testid="messagerie-msg-sender-name"
                        data-sender-key={senderKey}
                        className="ml-2 mb-0.5 text-[11px] font-semibold"
                        style={{ color: avatarColorFor(senderKey) }}
                    >
                        {senderName}
                    </span>
                ) : null}
                <div
                    className={
                        'relative rounded-2xl shadow-sm text-sm text-[#1A1D2E] ' +
                        (isOut ? 'rounded-br-md ' : 'rounded-bl-md ') +
                        bubbleBorder + ' ' +
                        (isStickerOnly ? 'p-0.5' : (hasMedia && !hasBody && !rich ? 'p-1' : 'px-3 py-2'))
                    }
                    style={{ backgroundColor: bubbleBg }}
                >
                    {isDeleted && !revealed ? (
                        <span
                            data-testid="messagerie-msg-deleted"
                            className="italic text-[#6B7280] inline-flex items-center gap-1"
                        >
                            <span aria-hidden>🚫</span>
                            Ce message a été supprimé
                        </span>
                    ) : (
                        <>
                            {hasReplyRef ? (
                                <QuotedPreview repliedTo={repliedTo} conversation={conversation} />
                            ) : null}
                            {rich && rich.type === 'location' ? (
                                <LocationCard data={rich.data} />
                            ) : rich && rich.type === 'contact' ? (
                                <ContactCard data={rich.data} />
                            ) : rich && rich.type === 'link_preview' ? (
                                <>
                                    <LinkPreviewCard data={rich.data} bodyFallback={message.body} />
                                    {hasBody && !(rich.data?.url && message.body && rich.data.url.includes(message.body)) ? (
                                        <div className="mt-1.5">
                                            <MessageBody text={message.body || ''} />
                                        </div>
                                    ) : null}
                                </>
                            ) : hasMedia ? (
                                <div className={hasBody ? 'mb-1.5' : ''}>
                                    <MediaAttachment
                                        media={message.media}
                                        onClick={() => openLightbox && openLightbox([message.media], 0)}
                                    />
                                </div>
                            ) : null}
                            {hasBody && !(rich && rich.type === 'link_preview') ? (
                                <span className="whitespace-pre-wrap break-words">
                                    <MessageBody text={message.body || ''} />
                                </span>
                            ) : null}
                            {!hasMedia && !hasBody && !rich ? (
                                <span className="italic text-[#6B7280]">[Message vide]</span>
                            ) : null}
                        </>
                    )}
                    {canReveal ? (
                        <button
                            type="button"
                            onClick={() => setShowOriginal((v) => !v)}
                            data-testid="messagerie-msg-reveal-deleted"
                            className="mt-1 text-[11px] underline text-[#4B3FD8] hover:text-[#3a2fb8] block"
                        >
                            {revealed ? 'Masquer le message' : 'Voir le message'}
                        </button>
                    ) : null}
                    <span
                        className={
                            'float-right ml-2 mt-0.5 text-[10px] tabular-nums inline-flex items-center gap-1 select-none ' +
                            (isOut ? 'text-[#4E7A2E]' : 'text-[#6B7280]')
                        }
                    >
                        <span>{casaTime(message.timestamp)}</span>
                        {isEdited && !isDeleted ? (
                            <span
                                data-testid="messagerie-msg-edited"
                                className="italic opacity-80"
                                title="Message modifié"
                            >
                                (modifié)
                            </span>
                        ) : null}
                        {isOut && <OutboundStatusIcon status={status} onRetry={handleRetry} />}
                    </span>
                    <div className="clear-both" />
                    {hasReactions ? (
                        <ReactionPills
                            reactions={message.reactions}
                            align={isOut ? 'end' : 'start'}
                            overlap
                        />
                    ) : null}
                </div>
                {!isDeleted ? (
                    <HoverMenu message={message} isOut={isOut} onReact={onReact} onReply={onReply} />
                ) : null}
                {hasReactionError ? (
                    <div
                        data-testid="messagerie-msg-reaction-error"
                        className={
                            'mt-1 text-[11px] text-[#EF4444] ' +
                            (isOut ? 'self-end' : 'self-start')
                        }
                    >
                        ⚠ Réaction non envoyée
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// C4 — Lightbox plein écran (images + vidéos).
// -----------------------------------------------------------------------
function Lightbox({ items, index, onClose, onChange }) {
    React.useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') onChange(Math.min(items.length - 1, index + 1));
            else if (e.key === 'ArrowLeft') onChange(Math.max(0, index - 1));
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [items.length, index, onClose, onChange]);

    const current = items[index];
    if (!current) return null;
    const href = current.r2_key ? messagerieApi.mediaHref(current.r2_key) : null;
    const kind = String(current.kind || '').toLowerCase();

    return createPortal(
        <div
            data-testid="messagerie-lightbox"
            className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center"
            onClick={onClose}
        >
            <button
                type="button"
                data-testid="messagerie-lightbox-close"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center"
                aria-label="Fermer"
            >
                <CloseIcon size={20} strokeWidth={2} />
            </button>
            {href ? (
                <a
                    href={href}
                    download
                    onClick={(e) => e.stopPropagation()}
                    data-testid="messagerie-lightbox-download"
                    className="absolute top-4 right-16 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center"
                    aria-label="Télécharger"
                    title="Télécharger"
                >
                    <Download size={18} strokeWidth={2} />
                </a>
            ) : null}
            {items.length > 1 ? (
                <>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onChange(Math.max(0, index - 1)); }}
                        disabled={index === 0}
                        data-testid="messagerie-lightbox-prev"
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Précédent"
                    >
                        <ChevronLeft size={22} strokeWidth={2} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onChange(Math.min(items.length - 1, index + 1)); }}
                        disabled={index === items.length - 1}
                        data-testid="messagerie-lightbox-next"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Suivant"
                    >
                        <ChevronRight size={22} strokeWidth={2} />
                    </button>
                </>
            ) : null}
            <div
                className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
            >
                {href ? (
                    kind === 'video' ? (
                        <video
                            src={href}
                            controls
                            autoPlay
                            className="max-w-full max-h-[90vh] rounded"
                        />
                    ) : (
                        <img
                            src={href}
                            alt="Média"
                            className="max-w-full max-h-[90vh] object-contain"
                        />
                    )
                ) : (
                    <div className="text-white text-sm">Média indisponible</div>
                )}
            </div>
            {items.length > 1 ? (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs tabular-nums bg-black/50 px-3 py-1 rounded-full">
                    {index + 1} / {items.length}
                </div>
            ) : null}
        </div>,
        document.body,
    );
}

// -----------------------------------------------------------------------
// Phase 4 — Panneau latéral participants (groupes, inchangé)
// -----------------------------------------------------------------------
function ParticipantsPanel({ group, loading, onClose }) {
    const participants = (group && group.participants) || [];
    const admins = participants.filter((p) => p.is_admin);
    const others = participants.filter((p) => !p.is_admin);
    return (
        <aside
            data-testid="messagerie-participants-panel"
            className="w-[320px] shrink-0 h-full border-l border-[#E5E7EB] bg-white flex flex-col"
        >
            <div className="h-14 shrink-0 px-4 border-b border-[#E5E7EB] flex items-center justify-between">
                <div className="min-w-0 flex items-center gap-2">
                    <Users size={16} strokeWidth={2} className="text-[#4B3FD8]" />
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1A1D2E] truncate">
                            Participants
                        </div>
                        <div className="text-[11px] text-[#6B7280]">
                            {loading ? 'Chargement…' : `${participants.length} participant${participants.length > 1 ? 's' : ''}`}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="messagerie-participants-close"
                    onClick={onClose}
                    className="w-7 h-7 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F5F6FA] inline-flex items-center justify-center"
                    title="Fermer"
                    aria-label="Fermer"
                >
                    <CloseIcon size={14} strokeWidth={2} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="p-4 text-center text-xs text-[#6B7280]">
                        Chargement des participants…
                    </div>
                ) : (participants.length === 0 ? (
                    <div className="p-4 text-center text-xs text-[#6B7280]">
                        Aucun participant connu pour ce groupe.
                    </div>
                ) : (
                    <>
                        {admins.length ? <ParticipantsGroup label="Administrateurs" people={admins} /> : null}
                        {others.length ? <ParticipantsGroup label="Membres" people={others} /> : null}
                    </>
                ))}
            </div>
        </aside>
    );
}

function ParticipantsGroup({ label, people }) {
    return (
        <div>
            <div className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">
                {label}
            </div>
            <ul className="divide-y divide-[#F3F4F6]">
                {people.map((p) => (
                    <li
                        key={p.wa_id || p.phone_number || Math.random()}
                        data-testid="messagerie-participant-row"
                        data-wa-id={p.wa_id || ''}
                        data-is-admin={p.is_admin ? 'true' : 'false'}
                        className="px-4 py-2 flex items-center gap-3"
                    >
                        <Avatar
                            name={p.display_name}
                            phone={p.phone_number}
                            waId={p.wa_id}
                            r2Key={p.profile_picture_r2_key}
                            sizeClass="w-8 h-8"
                            textClass="text-[11px]"
                            testId="messagerie-participant-avatar"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#1A1D2E] truncate">
                                {p.display_name || (p.phone_number ? `+${p.phone_number}` : 'Participant')}
                            </div>
                            {p.display_name && p.phone_number ? (
                                <div className="text-[11px] text-[#6B7280]">
                                    +{p.phone_number}
                                </div>
                            ) : null}
                        </div>
                        {p.is_admin ? (
                            <span
                                data-testid="messagerie-participant-admin-badge"
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-medium bg-[#4B3FD8]/10 text-[#4B3FD8] border border-[#4B3FD8]/30"
                                title="Administrateur"
                            >
                                <Shield size={10} strokeWidth={2.25} />
                                Admin
                            </span>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}
